// RAPID DEPLOYMENT — the reflex mode.
//
// Contacts converge on the island from open water. Tap them off the board
// before they land. This is the opposite of the main game on purpose: no
// numbers to optimise, no offline progress, just hands and a clock. It borrows
// the island silhouette from the theatre so it reads as the same world seen
// through a targeting scope rather than a spreadsheet.
import { ISLAND } from '../data/world.js';
import * as Fx from '../core/Fx.js';
import { modeShell, readoutMarkup, resultCard, clamp } from './chrome.js';

const TAU = Math.PI * 2;
const START_INTEGRITY = 100;
const COMBO_WINDOW = 2600;
const WAVE_BREAK = 2400;

const CONTACT_KINDS = [
  { id: 'skiff', label: 'FAST SKIFF', hp: 1, speed: 1.35, radius: 15, tone: 'cyan', score: 1 },
  { id: 'hull', label: 'HEAVY HULL', hp: 4, speed: 0.72, radius: 23, tone: 'amber', score: 3 },
  { id: 'ghost', label: 'GHOST TRACK', hp: 2, speed: 1.0, radius: 13, tone: 'red', score: 4 },
];

function centroid(points) {
  const sum = points.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);
  return [sum[0] / points.length, sum[1] / points.length];
}

const HOME = centroid(ISLAND);
// The theatre's island fills its panel edge to edge. Here it has to be a target
// sitting in open water with room for contacts to run at it, so the same
// silhouette is shrunk around its own centroid.
const ISLAND_SCALE = 0.4;
const CORE_RADIUS = 15;
const SPAWN_RX = 47;
const SPAWN_RY = 41;

function shrink([x, y]) {
  return [HOME[0] + (x - HOME[0]) * ISLAND_SCALE, HOME[1] + (y - HOME[1]) * ISLAND_SCALE];
}

const PLAY_ISLAND = ISLAND.map(shrink);

export const rapid = {
  id: 'rapid',
  name: 'Rapid Deployment',
  kicker: 'REFLEX / 5 MIN',
  tagline: 'Tap the contacts off the board before they land.',
  description: 'Waves converge on the island in real time. Every intercept builds a combo; every leak costs integrity. Pure hands — no idle progress, no penalty for losing.',
  create(ctx) {
    let raf = 0;
    let canvas = null;
    let dom = null;
    let run = null;
    let alive = true;

    function newRun() {
      return {
        contacts: [],
        wave: 0,
        spawnQueue: 0,
        nextSpawnAt: 0,
        waveStartsAt: performance.now() + 1200,
        integrity: START_INTEGRITY,
        score: 0,
        intercepts: 0,
        leaks: 0,
        combo: 0,
        bestCombo: 0,
        comboExpires: 0,
        over: false,
        startedAt: performance.now(),
      };
    }

    function waveSize(wave) { return 3 + Math.floor(wave * 1.6); }
    function waveSpeed(wave) { return 0.7 + wave * 0.08; }
    function spawnInterval(wave) { return Math.max(240, 900 - wave * 45); }

    function beginWave(now) {
      run.wave += 1;
      run.spawnQueue = waveSize(run.wave);
      run.nextSpawnAt = now;
      run.waveStartsAt = 0;
      Fx.banner(`WAVE ${run.wave}`, run.wave === 1 ? 'CONTACTS INBOUND' : `${run.spawnQueue} TRACKS`, 'cyan');
    }

    function spawnContact(now) {
      // Pick a kind, weighted so the nastier tracks only show up later.
      const pool = run.wave < 3 ? CONTACT_KINDS.slice(0, 2) : CONTACT_KINDS;
      const kind = pool[Math.floor(Math.random() * pool.length)];
      const angle = Math.random() * TAU;
      // Spawn on an ellipse just inside the viewport so every contact is
      // visible from the moment it appears — nothing arrives unannounced.
      const jitter = 0.92 + Math.random() * 0.08;
      run.contacts.push({
        kind,
        hp: kind.hp + Math.floor(run.wave / 3),
        maxHp: kind.hp + Math.floor(run.wave / 3),
        x: HOME[0] + Math.cos(angle) * SPAWN_RX * jitter,
        y: HOME[1] + Math.sin(angle) * SPAWN_RY * jitter,
        speed: kind.speed * waveSpeed(run.wave),
        born: now,
        hitAt: 0,
        wobble: Math.random() * TAU,
      });
      run.spawnQueue -= 1;
    }

    function registerHit(contact, clientX, clientY) {
      contact.hp -= 1;
      contact.hitAt = performance.now();
      if (contact.hp > 0) {
        Fx.burst(clientX, clientY, { count: 5, tone: contact.kind.tone, spread: 34 });
        return;
      }
      run.contacts.splice(run.contacts.indexOf(contact), 1);
      run.intercepts += 1;
      const now = performance.now();
      run.combo = now < run.comboExpires ? run.combo + 1 : 1;
      run.comboExpires = now + COMBO_WINDOW;
      run.bestCombo = Math.max(run.bestCombo, run.combo);
      const gained = contact.kind.score * (1 + run.combo * 0.15);
      run.score += gained;
      Fx.floatText(clientX, clientY - 10, `+${Math.round(gained)}${run.combo > 1 ? ` ×${run.combo}` : ''}`, { tone: contact.kind.tone, size: clamp(0.9 + run.combo * 0.06, 0.9, 1.9) });
      Fx.burst(clientX, clientY, { count: 12, tone: contact.kind.tone });
      Fx.ring(clientX, clientY, { tone: contact.kind.tone, size: 90 });
    }

    function registerLeak(contact) {
      run.contacts.splice(run.contacts.indexOf(contact), 1);
      run.leaks += 1;
      run.combo = 0;
      run.integrity -= 7 + run.wave;
      Fx.flash('red', 0.3);
      Fx.shake(9);
      if (run.integrity <= 0) finish();
    }

    function finish() {
      if (run.over) return;
      run.over = true;
      cancelAnimationFrame(raf);
      const score = Math.round(run.score);
      // A strong run is a deliberate active-play reward, but the payout is hard-
      // capped at 15 minutes of standing output so no single run can mint an
      // open-ended windfall relative to the main economy.
      const seconds = clamp(score * 1.6, 0, 900);
      const glimmer = clamp(Math.floor(run.wave * 1.5), 1, 40);
      const best = ctx.recordBest(score);
      ctx.commit({ seconds, glimmer, label: `Rapid Deployment · wave ${run.wave}` });
      Fx.flash(run.wave >= 6 ? 'amber' : 'cyan', 0.3);
      resultCard(dom.body, {
        title: run.integrity <= 0 ? 'The line went down on wave ' + run.wave + '.' : 'Contacts cleared.',
        lines: [
          ['Waves held', run.wave],
          ['Contacts intercepted', run.intercepts],
          ['Leaks', run.leaks],
          ['Best combo', `×${run.bestCombo}`],
          ['Score', score],
        ],
        seconds,
        glimmer,
        best,
      });
      wireResult();
    }

    function wireResult() {
      dom.body.querySelector('[data-role="again"]')?.addEventListener('click', startRun);
      dom.body.querySelector('[data-role="done"]')?.addEventListener('click', () => ctx.exit());
    }

    function toStage(px, py) {
      const rect = canvas.getBoundingClientRect();
      return [rect.width * px / 100, rect.height * py / 100];
    }

    function onPointer(event) {
      if (!run || run.over) return;
      const rect = canvas.getBoundingClientRect();
      const localX = ((event.clientX - rect.left) / rect.width) * 100;
      const localY = ((event.clientY - rect.top) / rect.height) * 100;
      // Nearest contact within its own radius wins the tap.
      let target = null;
      let bestDistance = Infinity;
      for (const contact of run.contacts) {
        const dx = (contact.x - localX) * (rect.width / 100);
        const dy = (contact.y - localY) * (rect.height / 100);
        const distance = Math.hypot(dx, dy);
        if (distance < contact.kind.radius + 12 && distance < bestDistance) {
          bestDistance = distance;
          target = contact;
        }
      }
      if (target) {
        registerHit(target, event.clientX, event.clientY);
      } else {
        run.combo = 0;
        Fx.ring(event.clientX, event.clientY, { tone: 'dim', size: 46 });
      }
    }

    function update(now) {
      if (run.over) return;
      if (run.waveStartsAt && now >= run.waveStartsAt) beginWave(now);
      if (!run.waveStartsAt && run.spawnQueue > 0 && now >= run.nextSpawnAt) {
        spawnContact(now);
        run.nextSpawnAt = now + spawnInterval(run.wave);
      }
      if (!run.waveStartsAt && run.spawnQueue <= 0 && run.contacts.length === 0) {
        run.waveStartsAt = now + WAVE_BREAK;
        run.integrity = Math.min(START_INTEGRITY, run.integrity + 6);
        Fx.banner('WAVE CLEAR', '+6 INTEGRITY RECOVERED', 'amber');
      }
      if (run.combo && now > run.comboExpires) run.combo = 0;
      for (const contact of run.contacts.slice()) {
        const dx = HOME[0] - contact.x;
        const dy = HOME[1] - contact.y;
        const distance = Math.hypot(dx, dy);
        if (distance < CORE_RADIUS) { registerLeak(contact); continue; }
        const step = contact.speed * 0.085;
        contact.x += (dx / distance) * step;
        contact.y += (dy / distance) * step * 0.72;
        contact.wobble += 0.05;
      }
    }

    function drawIsland(ctx2d, width, height) {
      ctx2d.beginPath();
      PLAY_ISLAND.forEach(([px, py], index) => {
        const [x, y] = [width * px / 100, height * py / 100];
        if (index === 0) ctx2d.moveTo(x, y); else ctx2d.lineTo(x, y);
      });
      ctx2d.closePath();
      const fill = ctx2d.createLinearGradient(0, height * 0.2, 0, height * 0.85);
      fill.addColorStop(0, 'rgba(44, 102, 95, .95)');
      fill.addColorStop(1, 'rgba(17, 48, 54, .95)');
      ctx2d.fillStyle = fill;
      ctx2d.fill();
      ctx2d.strokeStyle = 'rgba(145, 220, 196, .5)';
      ctx2d.lineWidth = 1.6;
      ctx2d.stroke();
    }

    function draw(now) {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      const ctx2d = canvas.getContext('2d');
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx2d.clearRect(0, 0, rect.width, rect.height);

      // Water
      const sea = ctx2d.createRadialGradient(rect.width * 0.5, rect.height * 0.5, 20, rect.width * 0.5, rect.height * 0.5, rect.width * 0.7);
      sea.addColorStop(0, '#0d2c37');
      sea.addColorStop(1, '#050f16');
      ctx2d.fillStyle = sea;
      ctx2d.fillRect(0, 0, rect.width, rect.height);

      // Approach envelope. The rings share the spawn ellipse's proportions, so
      // "how close is that one" is readable at a glance rather than guesswork.
      const [hx, hy] = toStage(HOME[0], HOME[1]);
      const rx = (fraction) => rect.width * (SPAWN_RX * fraction) / 100;
      const ry = (fraction) => rect.height * (SPAWN_RY * fraction) / 100;
      for (const fraction of [0.35, 0.6, 0.85]) {
        ctx2d.strokeStyle = `rgba(118, 224, 201, ${0.17 - fraction * 0.1})`;
        ctx2d.setLineDash([3, 10]);
        ctx2d.beginPath();
        ctx2d.ellipse(hx, hy, rx(fraction), ry(fraction), 0, 0, TAU);
        ctx2d.stroke();
      }
      ctx2d.setLineDash([]);

      // Sweeping radar arm
      const sweep = (now * 0.0011) % TAU;
      ctx2d.save();
      ctx2d.translate(hx, hy);
      ctx2d.rotate(sweep);
      const armLength = Math.max(rect.width, rect.height);
      const arm = ctx2d.createLinearGradient(0, 0, armLength, 0);
      arm.addColorStop(0, 'rgba(118, 224, 201, .22)');
      arm.addColorStop(1, 'rgba(118, 224, 201, 0)');
      ctx2d.fillStyle = arm;
      ctx2d.beginPath();
      ctx2d.moveTo(0, 0);
      ctx2d.arc(0, 0, armLength, -0.28, 0);
      ctx2d.closePath();
      ctx2d.fill();
      ctx2d.restore();

      drawIsland(ctx2d, rect.width, rect.height);

      // Contacts
      for (const contact of run.contacts) {
        const [x, y] = toStage(contact.x, contact.y);
        const tone = contact.kind.tone === 'amber' ? '255, 208, 138' : contact.kind.tone === 'red' ? '237, 146, 131' : '118, 224, 201';
        const hitFade = clamp(1 - (now - contact.hitAt) / 220, 0, 1);
        const radius = contact.kind.radius * (1 + hitFade * 0.28);

        // Track line back toward where it came from
        ctx2d.strokeStyle = `rgba(${tone}, .16)`;
        ctx2d.lineWidth = 1;
        ctx2d.beginPath();
        ctx2d.moveTo(x, y);
        const [tx, ty] = toStage(contact.x + (contact.x - HOME[0]) * 0.16, contact.y + (contact.y - HOME[1]) * 0.16);
        ctx2d.lineTo(tx, ty);
        ctx2d.stroke();

        ctx2d.strokeStyle = `rgba(${tone}, ${0.75 + hitFade * 0.25})`;
        ctx2d.lineWidth = 1.8;
        ctx2d.beginPath();
        ctx2d.arc(x, y, radius, 0, TAU);
        ctx2d.stroke();
        ctx2d.fillStyle = `rgba(${tone}, ${0.12 + hitFade * 0.4})`;
        ctx2d.fill();

        // Crosshair ticks
        ctx2d.beginPath();
        ctx2d.moveTo(x - radius - 5, y); ctx2d.lineTo(x - radius + 3, y);
        ctx2d.moveTo(x + radius - 3, y); ctx2d.lineTo(x + radius + 5, y);
        ctx2d.stroke();

        // Health pips for multi-hit tracks
        if (contact.maxHp > 1) {
          for (let index = 0; index < contact.maxHp; index += 1) {
            ctx2d.fillStyle = index < contact.hp ? `rgba(${tone}, .9)` : `rgba(${tone}, .2)`;
            ctx2d.fillRect(x - contact.maxHp * 3 + index * 6, y - radius - 9, 4, 3);
          }
        }
      }

      // Integrity reads as a shield drawn tight around the island itself.
      const shieldX = rect.width * (CORE_RADIUS + 4) / 100;
      const shieldY = rect.height * (CORE_RADIUS + 4) / 100;
      ctx2d.lineWidth = 4;
      ctx2d.strokeStyle = 'rgba(237, 146, 131, .16)';
      ctx2d.beginPath();
      ctx2d.ellipse(hx, hy, shieldX, shieldY, 0, 0, TAU);
      ctx2d.stroke();
      ctx2d.strokeStyle = run.integrity > 40 ? 'rgba(118, 224, 201, .85)' : 'rgba(237, 146, 131, .95)';
      ctx2d.beginPath();
      ctx2d.ellipse(hx, hy, shieldX, shieldY, 0, -Math.PI / 2, -Math.PI / 2 + TAU * (run.integrity / START_INTEGRITY));
      ctx2d.stroke();
    }

    function syncReadout() {
      const comboTone = run.combo >= 5 ? 'stat-hot' : '';
      dom.readout.innerHTML = readoutMarkup([
        ['WAVE', run.wave || '—'],
        ['INTEGRITY', `${Math.max(0, Math.round(run.integrity))}%`, run.integrity <= 40 ? 'stat-danger' : ''],
        ['COMBO', run.combo ? `×${run.combo}` : '—', comboTone],
        ['SCORE', Math.round(run.score)],
      ]);
    }

    function loop(now) {
      if (!alive || run.over) return;
      update(now);
      draw(now);
      syncReadout();
      raf = requestAnimationFrame(loop);
    }

    function startRun() {
      run = newRun();
      dom.body.innerHTML = `<div class="rapid-stage"><canvas data-role="canvas"></canvas><p class="rapid-hint">TAP THE CONTACTS · HEAVY HULLS TAKE SEVERAL HITS · A MISS BREAKS YOUR COMBO</p></div>`;
      canvas = dom.body.querySelector('[data-role="canvas"]');
      canvas.addEventListener('pointerdown', onPointer);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(loop);
    }

    return {
      mount(root) {
        dom = modeShell(root, rapid);
        dom.exit.addEventListener('click', () => ctx.exit());
        startRun();
      },
      destroy() {
        alive = false;
        cancelAnimationFrame(raf);
        canvas?.removeEventListener('pointerdown', onPointer);
      },
    };
  },
};
